/**
 * V205 — Fidelidad §29 en la COMANDA (flujo A): el cliente muestra su código (o dicta su
 * teléfono) ANTES de cobrar; el cajero lo teclea/escanea aquí y queda ligado a la cuenta.
 * Al cobrarla pagada el backend pone UN sello (idempotente). Un lector de QR/código de
 * barras USB funciona como teclado: escribe el código y manda Enter.
 *  - Avance de la cartilla + recompensas ganadas (no expiran) con botón "Canjear" →
 *    ticket de promoción $0 (documento aparte; no toca esta cuenta).
 *  - "Lo de siempre" (D3): solo si el tenant lo encendió (apagado por defecto).
 * Si el programa está apagado, el componente no pinta nada.
 */
import { useEffect, useState } from 'react';
import { Gift, Sparkles, X } from 'lucide-react';
import { api } from '../lib/api';
import { formatLoyaltyCode, looksLikePhone } from './loyaltyCode';

export interface OrderLoyalty {
  memberId: string; code: string; codeDisplay: string; stamped: boolean;
  stamps: number; required: number; rewardReady: boolean;
  rewardLabel: string | null; ticketLine: string | null;
}
interface MemberProgress {
  memberId: string; code: string; codeDisplay: string; hasPhone: boolean;
  current: { campaignName: string; rewardLabel: string; stamps: number; required: number; partialsExpireAt: string | null } | null;
  completed: Array<{ cardId: string; campaignName: string; rewardLabel: string; completedAt: string }>;
  usualOrderEnabled: boolean;
  /** V206 — se negó a "ofertas según tu historial" ("lo de siempre" no le sugiere). */
  historyOptOut?: boolean;
}
interface UsualItem { productId: string; variantId: string | null; description: string; times: number }

// Sin caché a propósito: encender/apagar el programa en Configuración debe verse en la
// siguiente comanda que se abra, sin recargar la app.
const loyaltyStatus = () =>
  (api('/loyalty/pos/status') as Promise<{ enabled: boolean; usualOrderEnabled: boolean }>)
    .catch(() => ({ enabled: false, usualOrderEnabled: false }));

const errMsg = (e: any, fallback: string) => e?.body?.message || e?.message || fallback;

export function LoyaltyPosBox({
  order, sourceModule, disabled, onOrder, onAddUsual, onPromoTicket,
}: {
  order: { id: string; status: string; loyalty?: OrderLoyalty | null; isLoyaltyCourtesy?: boolean };
  sourceModule: string;
  disabled?: boolean;
  onOrder: (o: any) => void;
  onAddUsual: (u: { productId: string; variantId: string | null }) => void;
  onPromoTicket: (o: any) => void;
}) {
  const [enabled, setEnabled] = useState(false);
  const [input, setInput] = useState('');
  const [member, setMember] = useState<MemberProgress | null>(null);
  const [usual, setUsual] = useState<UsualItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => { loyaltyStatus().then((s) => setEnabled(s.enabled)); }, []);

  const attachedCode = order.loyalty?.code ?? null;

  // Cuenta reabierta con miembro ya ligado → recarga su cartilla completa.
  useEffect(() => {
    if (!enabled || !attachedCode || member?.code === attachedCode) return;
    let alive = true;
    (api('/loyalty/pos/identify', { method: 'POST', body: { code: attachedCode } }) as Promise<MemberProgress>)
      .then((m) => { if (alive) setMember(m); })
      .catch(() => {});
    return () => { alive = false; };
  }, [enabled, attachedCode, member?.code]);

  useEffect(() => {
    if (!member?.usualOrderEnabled) { setUsual([]); return; }
    let alive = true;
    (api(`/loyalty/pos/members/${member.memberId}/usual`) as Promise<UsualItem[]>)
      .then((u) => { if (alive) setUsual(u); })
      .catch(() => { if (alive) setUsual([]); });
    return () => { alive = false; };
  }, [member?.memberId, member?.usualOrderEnabled]);

  if (!enabled || order.isLoyaltyCourtesy || order.status !== 'OPEN') return null;

  const identify = async () => {
    const raw = input.trim();
    if (!raw) return;
    setBusy(true); setMsg(null);
    try {
      const body = looksLikePhone(raw) ? { phone: raw } : { code: raw };
      const m = await api('/loyalty/pos/identify', { method: 'POST', body }) as MemberProgress;
      const o = await api(`/loyalty/pos/orders/${order.id}/member`, { method: 'POST', body: { memberId: m.memberId } });
      setMember(m); setInput(''); onOrder(o);
    } catch (e: any) {
      setMsg(errMsg(e, 'No encontramos ese cliente.'));
    } finally { setBusy(false); }
  };

  const detach = async () => {
    setBusy(true); setMsg(null);
    try {
      const o = await api(`/loyalty/pos/orders/${order.id}/member`, { method: 'POST', body: { memberId: null } });
      setMember(null); setUsual([]); onOrder(o);
    } catch (e: any) { setMsg(errMsg(e, 'No se pudo quitar el cliente.')); } finally { setBusy(false); }
  };

  // V206 — BAJA en el mostrador: elimina teléfono y cartilla; esta cuenta queda sin miembro.
  const deleteMember = async () => {
    if (!member) return;
    setBusy(true); setMsg(null);
    try {
      await api(`/loyalty/pos/members/${member.memberId}/delete`, { method: 'POST', body: { confirm: true } });
      const o = await api(`/pos/orders/${order.id}`);
      setMember(null); setUsual([]); setConfirmDelete(false); onOrder(o);
      setMsg('Cliente dado de baja: se eliminaron su teléfono y su cartilla.');
    } catch (e: any) { setMsg(errMsg(e, 'No se pudo dar de baja.')); } finally { setBusy(false); }
  };

  const redeem = async (cardId: string) => {
    setBusy(true); setMsg(null);
    try {
      const promo = await api('/loyalty/pos/redeem', { method: 'POST', body: { cardId, sourceModule } });
      const m = await api('/loyalty/pos/identify', { method: 'POST', body: { code: member!.code } }) as MemberProgress;
      setMember(m);
      onPromoTicket(promo);
    } catch (e: any) { setMsg(errMsg(e, 'No se pudo canjear.')); } finally { setBusy(false); }
  };

  const cur = member?.current ?? null;

  return (
    <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3" data-testid="loyalty-pos-box">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
        <Gift className="h-3.5 w-3.5" /> Cliente de lealtad
      </p>
      {!attachedCode ? (
        <div className="mt-2 flex gap-2">
          <input className="input min-w-0 flex-1 text-sm" value={input} disabled={busy || disabled}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); identify(); } }}
            placeholder="Código (ABCD-2346) o teléfono" aria-label="Código o teléfono del cliente de lealtad" />
          <button type="button" onClick={identify} disabled={busy || disabled || !input.trim()}
            className="shrink-0 rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
            Buscar
          </button>
        </div>
      ) : (
        <div className="mt-1.5 text-sm">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono font-semibold tracking-wider text-emerald-900">{formatLoyaltyCode(attachedCode)}</span>
            <button type="button" onClick={detach} disabled={busy || disabled}
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-rose-600" title="Quitar cliente de esta cuenta">
              <X className="h-3 w-3" /> Quitar
            </button>
          </div>
          {cur ? (
            <p className="text-xs text-emerald-800">
              {cur.stamps} de {cur.required} visitas · {cur.rewardLabel}
              <span className="block text-[11px] text-emerald-700">Esta cuenta suma 1 sello al cobrarse.</span>
            </p>
          ) : <p className="text-xs text-slate-500">Sin campaña vigente: esta cuenta no suma sello.</p>}
          {(member?.completed ?? []).map((c) => (
            <div key={c.cardId} className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-white px-2 py-1.5 ring-1 ring-amber-200">
              <span className="text-xs text-amber-800">Recompensa ganada: <strong>{c.rewardLabel}</strong></span>
              <button type="button" onClick={() => redeem(c.cardId)} disabled={busy || disabled}
                className="shrink-0 rounded-lg bg-amber-500 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-amber-600 disabled:opacity-50">
                Canjear
              </button>
            </div>
          ))}
          {member?.historyOptOut && (
            <p className="mt-1 text-[11px] text-slate-500">Se negó a ofertas según su historial (sin "lo de siempre").</p>
          )}
          {usual.length > 0 && (
            <div className="mt-2">
              <p className="flex items-center gap-1 text-[11px] font-medium text-slate-600"><Sparkles className="h-3 w-3" /> Lo de siempre</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {usual.map((u) => (
                  <button key={`${u.productId}:${u.variantId ?? ''}`} type="button" disabled={busy || disabled}
                    onClick={() => onAddUsual({ productId: u.productId, variantId: u.variantId })}
                    className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">
                    + {u.description}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {attachedCode && member && (!confirmDelete ? (
        <button type="button" onClick={() => setConfirmDelete(true)} disabled={busy || disabled}
          className="mt-2 text-[11px] text-rose-700 underline">Dar de baja al cliente</button>
      ) : (
        <div className="mt-2 rounded-lg bg-rose-50 p-2 text-[11px] text-rose-800 ring-1 ring-rose-200">
          <p>La baja elimina su teléfono y su cartilla (sellos e historial). No se puede deshacer.</p>
          {member.completed.length > 0 && (
            <p className="mt-1 font-semibold">Tiene {member.completed.length} recompensa(s) sin canjear: dejarán de estar disponibles. Ofrécele canjearlas antes.</p>
          )}
          <div className="mt-1.5 flex gap-2">
            <button type="button" onClick={deleteMember} disabled={busy}
              className="rounded-lg bg-rose-600 px-2.5 py-1 font-semibold text-white disabled:opacity-50">Confirmar baja</button>
            <button type="button" onClick={() => setConfirmDelete(false)} className="rounded-lg bg-white px-2.5 py-1 ring-1 ring-slate-200">Cancelar</button>
          </div>
        </div>
      ))}
      {msg && <p className="mt-2 text-[11px] text-rose-700">{msg}</p>}
    </div>
  );
}
